/* Cliente del API. Adjunta el token y la empresa activa a cada petición y
   traduce los errores del backend (que ya vienen explicados) a un tipo propio. */

const BASE = import.meta.env.VITE_API_URL ?? '';
const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);

// El iframe de la vista previa puede bloquear localStorage: se accede con red.
const store = {
  get(k: string): string | null {
    try { return localStorage.getItem(k); } catch { return null; }
  },
  set(k: string, v: string) {
    try { localStorage.setItem(k, v); } catch { /* memoria únicamente */ }
  },
  del(k: string) {
    try { localStorage.removeItem(k); } catch { /* memoria únicamente */ }
  },
};

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// En vista previa se entra directamente, con una sesión ya iniciada.
let token: string | null = PREVIEW ? 'preview' : store.get('nexo.token');
let companyId: string | null = store.get('nexo.company');

export const auth = {
  get token() {
    return token;
  },
  get companyId() {
    return companyId;
  },
  setToken(t: string | null) {
    token = t;
    if (t) store.set('nexo.token', t);
    else store.del('nexo.token');
  },
  setCompany(id: string | null) {
    companyId = id;
    if (id) store.set('nexo.company', id);
    else store.del('nexo.company');
  },
};

// Puente a la simulación en el navegador (solo se incluye en el build de preview).
let mockLoader: Promise<{ mockRequest: <T>(m: string, p: string, b?: unknown) => Promise<T> }> | null = null;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (PREVIEW) {
    mockLoader ??= import('../preview/mock');
    const m = await mockLoader;
    return m.mockRequest<T>(method, path, body);
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;
  if (companyId) headers['x-company-id'] = companyId;

  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'No se pudo contactar con el servidor. ¿Está el backend en marcha?');
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.error ?? 'error',
      data?.message ?? 'Error inesperado.',
      data?.details,
    );
  }
  return data as T;
}

/**
 * Descarga un PDF (u otro binario) enviando la credencial. Un `window.open`
 * directo no la envía y el servidor respondería 401: por eso se pide con
 * fetch y se entrega como blob.
 */
async function fetchBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (companyId) headers['x-company-id'] = companyId;

  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, { method: 'GET', headers });
  } catch {
    throw new ApiError(0, 'network', 'No se pudo contactar con el servidor. ¿Está el backend en marcha?');
  }
  if (!res.ok) {
    let message = 'No se pudo generar el documento.';
    try { const t = await res.text(); if (t) message = JSON.parse(t)?.message ?? message; } catch { /* binario */ }
    throw new ApiError(res.status, 'error', message);
  }
  return res.blob();
}

export const files = {
  /** URL temporal en memoria para mostrar el PDF dentro de la aplicación. */
  objectUrl: async (path: string): Promise<string> => URL.createObjectURL(await fetchBlob(path)),
  /** Descarga con nombre de archivo, sin salir de la sesión. */
  download: async (path: string, filename: string): Promise<void> => {
    const url = URL.createObjectURL(await fetchBlob(path));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  },
};

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

/* ─────────────────────────── Formas de datos ─────────────────────────── */

export type ObjectiveStatus =
  | 'active'
  | 'locked_plan'
  | 'not_applicable'
  | 'needs_setup'
  | 'no_permission'
  | 'license_missing'
  | 'license_expired'
  | 'plan_suspended';

export interface ObjectiveAction {
  kind: 'upgrade' | 'setup' | 'license' | 'role' | 'billing';
  label: string;
  to?: string;
}

export interface EvaluatedObjective {
  objective: {
    id: string;
    module: string;
    title: string;
    detail: string;
    route?: string;
  };
  status: ObjectiveStatus;
  reason: string;
  action?: ObjectiveAction;
  metric?: { key: string; label: string; value: number; target?: number; onTarget?: boolean };
}

export interface NavItem {
  id: string;
  label: string;
  route: string;
  objectives: string[];
  showLocked?: boolean;
  badge?: string;
  enabled: boolean;
  lockedReason?: string;
}
export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export interface CapabilityReport {
  plan: { code: string; name: string; status: string };
  role: string;
  roleLabel: string;
  sectors: string[];
  license: {
    provided: boolean;
    valid: boolean;
    daysToExpiry?: number;
    expiryDate?: string | null;
    activities: string[];
    number?: string;
    authority?: string;
  };
  limits: Record<string, number>;
  usage: Record<string, number>;
  limitWarnings: Array<{ key: string; used: number; limit: number; reached: boolean }>;
  objectives: EvaluatedObjective[];
  summary: Record<ObjectiveStatus, number>;
  activeIds: string[];
  modules: Array<{ module: string; label: string; active: number; total: number }>;
}

/** /capabilities devuelve el informe con los campos al primer nivel + extras. */
export type CapabilitiesResponse = CapabilityReport & {
  navigation: NavSection[];
  quickActions: Array<{ id: string; label: string; route: string; objective: string }>;
  statusLabels: Record<ObjectiveStatus, string>;
  moduleLabels: Record<string, string>;
};

export interface Company {
  id: string;
  legalName: string;
  tradeName: string;
  jurisdiction: string;
  currency: string;
  licenseNumber: string | null;
  licenseExpiryDate?: string | null;
}

export interface Me {
  user: { id: string; name: string; email: string; locale?: string };
  workspace: { id: string; name: string; plan: string; planName: string; planStatus: string };
  role: string;
  roleLabel: string;
  activeCompanyId?: string | null;
  companies: Company[];
}
